import type {
  ModelRelease,
  Openness,
  ReleaseStatus,
  TimelineRelease
} from "../types/model";
import {
  getCuratedModelTags,
  type CuratedModelTag
} from "../data/curated-models";
import { entryModelCount } from "./release-groups";

export type GroupMode = "date" | "company" | "category" | "none";
export type SortMode =
  | "newest"
  | "oldest"
  | "name"
  | "latest"
  | "count"
  | "company"
  | "category"
  | "status";

export interface ReleaseFilters {
  curatedTags: CuratedModelTag[];
  companies: string[];
  categories: string[];
  capabilities: string[];
  statuses: ReleaseStatus[];
  openness: Openness[];
  from: string;
  to: string;
}

export type ReleaseFilterListKey =
  | "curatedTags"
  | "companies"
  | "categories"
  | "capabilities"
  | "statuses"
  | "openness";

export type ReleaseTagFilterKey =
  | "curatedTags"
  | "categories"
  | "statuses"
  | "openness";

export interface ReleaseTagFilter {
  key: ReleaseTagFilterKey;
  value: string;
}

export interface ReleaseTagFilterInteraction {
  originModelId: string;
  source: "release-tag";
}

export const EMPTY_FILTERS: ReleaseFilters = {
  curatedTags: [],
  companies: [],
  categories: [],
  capabilities: [],
  statuses: [],
  openness: [],
  from: "",
  to: ""
};

export const SORT_OPTIONS: Record<
  GroupMode,
  Array<{ value: SortMode; label: string }>
> = {
  date: [
    { value: "newest", label: "Newest first" },
    { value: "oldest", label: "Oldest first" }
  ],
  company: [
    { value: "name", label: "Company name" },
    { value: "latest", label: "Latest release" },
    { value: "count", label: "Model count" }
  ],
  category: [
    { value: "name", label: "Category name" },
    { value: "latest", label: "Latest release" },
    { value: "count", label: "Model count" }
  ],
  none: [
    { value: "newest", label: "Newest first" },
    { value: "oldest", label: "Oldest first" },
    { value: "name", label: "Model name" },
    { value: "company", label: "Company" },
    { value: "category", label: "Category" },
    { value: "status", label: "Release status" }
  ]
};

const STATUS_RANK: Record<ReleaseStatus, number> = {
  Preview: 0,
  Research: 1,
  Released: 2,
  Deprecated: 3,
  Retired: 4,
  Unverified: 5,
  Other: 6
};

const FLAGSHIP_SEARCH_TERMS = new Set(["flagship", "旗舰", "旗舰模型"]);
const MILESTONE_SEARCH_TERMS = new Set(["milestone", "里程碑", "里程碑模型"]);

// Model names carry their own punctuation — `gpt-4o`, `qwen3.7-flash`,
// `inclusionAI/Ling-3.0-flash` — so the index and the query are cut into words
// on everything except the characters that appear inside a version number.
const WORD_SEPARATOR_PATTERN = /[^\p{L}\p{N}.+]+/gu;
const CJK_PATTERN = /[぀-ヿ㐀-䶿一-鿿豈-﫿가-힯]/u;

type SearchTerm =
  | { kind: "curated"; tag: CuratedModelTag }
  /** Scripts without word separators keep the substring matching they rely on. */
  | { kind: "substring"; value: string }
  /** Words that have to appear consecutively in the index. */
  | { kind: "phrase"; words: string[] };

function toIndexWords(value: string): string[] {
  return value.replace(WORD_SEPARATOR_PATTERN, " ").split(" ").filter(Boolean);
}

/**
 * Compile a query into the terms every matching record has to satisfy.
 *
 * A separator the owner typed carries no meaning of its own, so a query
 * without curated tags or unsegmented script is one phrase: `gen 2` and
 * `gen-2` are the same query and both reach Gen-2 exactly. The consequence is
 * that a query has to be a contiguous run of the record's own words — `qwen
 * pro` does not reach `Qwen-Image-3.0-Pro`, because the version number sits
 * between them.
 */
export function compileSearchQuery(query: string): SearchTerm[] {
  const tokens = query
    .trim()
    .toLocaleLowerCase("en")
    .split(/\s+/u)
    .filter(Boolean);
  const terms: SearchTerm[] = [];
  const plain: string[] = [];
  for (const token of tokens) {
    if (FLAGSHIP_SEARCH_TERMS.has(token)) {
      terms.push({ kind: "curated", tag: "flagship" });
    } else if (MILESTONE_SEARCH_TERMS.has(token)) {
      terms.push({ kind: "curated", tag: "milestone" });
    } else {
      plain.push(token);
    }
  }

  if (plain.some((token) => CJK_PATTERN.test(token))) {
    // A query reaching into an unsegmented script cannot be one phrase, since
    // its words are not separated in the index either. Those queries keep
    // matching token by token, which is the behavior they already had.
    for (const token of plain) {
      if (CJK_PATTERN.test(token)) {
        terms.push({ kind: "substring", value: token });
        continue;
      }
      const words = toIndexWords(token);
      if (words.length) terms.push({ kind: "phrase", words });
    }
    return terms;
  }

  const words = toIndexWords(plain.join(" "));
  if (words.length) terms.push({ kind: "phrase", words });
  return terms;
}

/**
 * Match a run of `sequence` against `words`. Every word but the last has to
 * match in full; the last is a prefix so that typing `gen` still reaches
 * `generation` and `o3-mini` still reaches `o3-mini-high`.
 */
function matchesWordSequence(words: string[], sequence: string[]): boolean {
  if (!sequence.length) return true;
  const lastIndex = sequence.length - 1;
  for (let start = 0; start + sequence.length <= words.length; start += 1) {
    let matched = true;
    for (let offset = 0; offset < lastIndex; offset += 1) {
      if (words[start + offset] !== sequence[offset]) {
        matched = false;
        break;
      }
    }
    if (matched && words[start + lastIndex].startsWith(sequence[lastIndex])) {
      return true;
    }
  }
  return false;
}

function matchesSearchTerm(
  record: ModelRelease,
  searchText: string,
  searchWords: string[],
  term: SearchTerm
): boolean {
  if (term.kind === "curated") {
    return getCuratedModelTags(record.id).includes(term.tag);
  }
  if (term.kind === "substring") return searchText.includes(term.value);
  return matchesWordSequence(searchWords, term.words);
}

export function countActiveFilters(filters: ReleaseFilters): number {
  return (
    filters.curatedTags.length +
    filters.companies.length +
    filters.categories.length +
    filters.capabilities.length +
    filters.statuses.length +
    filters.openness.length +
    (filters.from || filters.to ? 1 : 0)
  );
}

export function toggleReleaseFilterValue(
  filters: ReleaseFilters,
  key: ReleaseFilterListKey,
  value: string
): ReleaseFilters {
  const values = filters[key] as readonly string[];
  return {
    ...filters,
    [key]: values.includes(value)
      ? values.filter((item) => item !== value)
      : [...values, value]
  } as ReleaseFilters;
}

export function filterModels(
  records: ModelRelease[],
  query: string,
  filters: ReleaseFilters,
  searchTextForRecord: (record: ModelRelease) => string = (record) =>
    record.searchText
): ModelRelease[] {
  const terms = compileSearchQuery(query);
  // Splitting the index into words costs nothing to skip when every term is
  // matched as a substring anyway.
  const needsWords = terms.some((term) => term.kind === "phrase");
  return records.filter((record) => {
    const searchText = terms.length ? searchTextForRecord(record) : "";
    const searchWords = needsWords ? toIndexWords(searchText) : [];
    if (
      terms.length &&
      !terms.every((term) =>
        matchesSearchTerm(record, searchText, searchWords, term)
      )
    ) {
      return false;
    }
    if (
      filters.curatedTags.length &&
      !getCuratedModelTags(record.id).some((tag) =>
        filters.curatedTags.includes(tag)
      )
    ) {
      return false;
    }
    if (
      filters.companies.length &&
      !record.organization.filterIds.some((organizationId) =>
        filters.companies.includes(organizationId)
      )
    ) {
      return false;
    }
    if (
      filters.categories.length &&
      !filters.categories.includes(record.mainCategory)
    ) {
      return false;
    }
    if (
      filters.capabilities.length &&
      !record.capabilities.some((item) => filters.capabilities.includes(item))
    ) {
      return false;
    }
    if (
      filters.statuses.length &&
      !filters.statuses.includes(record.release.status)
    ) {
      return false;
    }
    if (
      filters.openness.length &&
      !filters.openness.includes(record.release.openness)
    ) {
      return false;
    }
    if (filters.from && (!record.dates.end || record.dates.end < filters.from)) {
      return false;
    }
    if (filters.to && (!record.dates.start || record.dates.start > filters.to)) {
      return false;
    }
    return true;
  });
}

export function sortModels<T extends TimelineRelease>(
  records: T[],
  group: GroupMode,
  sort: SortMode
): T[] {
  // Count models, not cards: a group card adds its matching members.
  const companyCounts = new Map<string, number>();
  const categoryCounts = new Map<string, number>();
  for (const record of records) {
    const company = record.organization.groupId;
    const count = entryModelCount(record);
    companyCounts.set(company, (companyCounts.get(company) ?? 0) + count);
    categoryCounts.set(
      record.mainCategory,
      (categoryCounts.get(record.mainCategory) ?? 0) + count
    );
  }

  const newest = (left: ModelRelease, right: ModelRelease) =>
    (right.dates.start ?? "").localeCompare(left.dates.start ?? "") ||
    left.name.localeCompare(right.name);
  const oldest = (left: ModelRelease, right: ModelRelease) =>
    (left.dates.start ?? "9999").localeCompare(right.dates.start ?? "9999") ||
    left.name.localeCompare(right.name);

  return records.toSorted((left, right) => {
    if (group === "date") return sort === "oldest" ? oldest(left, right) : newest(left, right);
    if (group === "company") {
      if (sort === "count") {
        const byCount =
          (companyCounts.get(right.organization.groupId) ?? 0) -
          (companyCounts.get(left.organization.groupId) ?? 0);
        if (byCount) return byCount;
      }
      if (sort === "latest") return newest(left, right);
      return left.organization.groupId.localeCompare(right.organization.groupId) || newest(left, right);
    }
    if (group === "category") {
      if (sort === "count") {
        const byCount =
          (categoryCounts.get(right.mainCategory) ?? 0) -
          (categoryCounts.get(left.mainCategory) ?? 0);
        if (byCount) return byCount;
      }
      if (sort === "latest") return newest(left, right);
      return (
        left.mainCategory.localeCompare(right.mainCategory) || newest(left, right)
      );
    }
    if (sort === "oldest") return oldest(left, right);
    if (sort === "name") return left.name.localeCompare(right.name);
    if (sort === "company") {
      return left.organization.groupId.localeCompare(right.organization.groupId) || newest(left, right);
    }
    if (sort === "category") {
      return left.mainCategory.localeCompare(right.mainCategory) || newest(left, right);
    }
    if (sort === "status") {
      return (
        STATUS_RANK[left.release.status] - STATUS_RANK[right.release.status] ||
        newest(left, right)
      );
    }
    return newest(left, right);
  });
}
